/** Mandantenspezifische Shopify-Verbindung (Einstellungen → Integrationen).
 *  Zwei Ebenen:
 *   - PLATFORM/GFC: Credentials aus sicherer Server-Konfiguration (read-only Status).
 *   - ASSOCIATION/TENANT: Auth-Modus wählbar (gleiche Organisation = Client-Credentials, oder externer Shop = OAuth).
 *  Secrets werden nie zurückgeliefert — Anzeige ausschließlich maskiert. */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Trash2, PlugZap, Server, AlertTriangle } from "lucide-react";

const EMPTY = { shop_domain: "", api_version: "2025-01", environment: "test",
  prod_shop_domain: "", auth_mode: "client_credentials", client_id: "", client_secret: "",
  webhook_secret: "", active: true };

export default function ShopifyConnectionTab({ canEdit }) {
  const { t } = useI18n();
  const [info, setInfo] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(() => {
    api.get("/shopify/connection").then((r) => {
      setInfo(r.data);
      setF({ ...EMPTY, shop_domain: r.data.shop_domain || "", api_version: r.data.api_version || "2025-01",
        environment: r.data.environment || "test",
        auth_mode: (r.data.auth_mode === "oauth" || r.data.auth_mode === "client_credentials") ? r.data.auth_mode : "client_credentials",
        client_id: r.data.client_id || "",
        prod_shop_domain: r.data.prod_shop_domain || "",
        active: r.data.source ? r.data.active !== false : true });
    }).catch(() => setInfo({ configured: false, source: null }));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/shopify/connection", {
        shop_domain: f.shop_domain, api_version: f.api_version, environment: f.environment,
        prod_shop_domain: f.prod_shop_domain, active: f.active, auth_mode: f.auth_mode,
        client_id: f.client_id || null, client_secret: f.client_secret || null,
        webhook_secret: f.webhook_secret || null });
      toast.success(t("sc.saved"));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(t("sc.deleteConfirm"))) return;
    try { await api.delete("/shopify/connection"); toast.success(t("common.saved")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runTest = async () => {
    setTesting(true);
    try { const r = await api.get("/shopify/test-connection"); setTest(r.data); }
    catch (e) { setTest({ ok: false, detail: apiError(e) }); }
    finally { setTesting(false); }
  };

  if (!info) return <div className="text-muted-foreground">{t("common.loading")}</div>;

  const serverManaged = info.source === "env" && info.managed === "server" && !info.config_error;
  const partialError = info.config_error === "partial_client_credentials";
  const envLegacy = info.source === "env" && !info.managed;

  const TestButton = (
    <div>
      <Button variant="outline" size="sm" disabled={testing} data-testid="sc-test-btn" onClick={runTest}>
        <PlugZap className={`h-4 w-4 mr-1.5 ${testing ? "animate-pulse" : ""}`} /> {t("sc.testBtn")}
      </Button>
      {test && (
        <div className={`mt-2 rounded-md border px-3 py-2 text-sm ${test.ok ? "border-primary/40 bg-primary/5 text-primary" : "border-destructive/40 bg-destructive/5 text-destructive"}`} data-testid="sc-test-result">
          <div className="font-medium">{test.ok ? t("sc.testOk") : t("sc.testFail")}</div>
          {test.ok ? (
            <div className="mt-0.5 text-xs text-foreground">
              {t("sc.testShop")}: <span className="font-medium">{test.shop_name}</span>
              <span className="ml-2 font-mono">{test.myshopify_domain}</span>
              {test.plan && <span className="ml-2">· {test.plan}</span>}
              {test.currency && <span className="ml-2 font-mono">{test.currency}</span>}
            </div>
          ) : <div className="mt-0.5 text-xs">{test.detail}</div>}
          {test.scopes && (
            <div className="mt-1.5 text-xs text-muted-foreground" data-testid="sc-test-scopes">
              {t("sc.testScopes")}: <span className="font-mono">{test.scopes.length ? test.scopes.join(", ") : "—"}</span>
              {test.missing_scopes?.length > 0 && (
                <div className="mt-0.5 text-destructive" data-testid="sc-test-scopes-missing">
                  {t("sc.testScopesMissing")}: <span className="font-mono">{test.missing_scopes.join(", ")}</span>
                </div>
              )}
              {test.all_orders_hint && <div className="mt-0.5">{t("sc.allOrdersHint")}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-xl space-y-4" data-testid="shopify-connection-tab">
      <div>
        <h3 className="font-heading font-medium">{t("sc.title")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t("sc.hint")}</p>
      </div>

      {serverManaged ? (
        <div className="space-y-4" data-testid="sc-server-managed">
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium text-primary">
              <Server className="h-4 w-4" /> {t("sc.serverManaged")}
            </div>
            <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              <div>{t("sc.authMode")}: <span className="text-foreground">{t("sc.authSameOrg")}</span></div>
              {info.shop_domain && <div>{t("sc.domain")}: <span className="font-mono text-foreground">{info.shop_domain}</span></div>}
              <div>{t("sc.apiVersion")}: <span className="font-mono text-foreground">{info.api_version}</span></div>
              <div className="text-primary/80">{t("sc.serverManagedNote")}</div>
            </div>
          </div>
          {TestButton}
        </div>
      ) : partialError ? (
        <div className="space-y-4" data-testid="sc-config-error">
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <div className="flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4" /> {t("sc.configError")}
            </div>
            <div className="mt-1 text-xs">{t("sc.partialCreds")}</div>
          </div>
        </div>
      ) : envLegacy ? (
        <div className="space-y-4" data-testid="sc-env-legacy">
          <div className="rounded-md border border-chart-4/40 bg-chart-4/10 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium text-chart-4">
              <Server className="h-4 w-4" /> {t("sc.sourceEnv")}
            </div>
            <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              <div>{t("sc.authMode")}: <span className="text-foreground">{t("sc.authToken")}</span></div>
              {info.shop_domain && <div>{t("sc.domain")}: <span className="font-mono text-foreground">{info.shop_domain}</span></div>}
              <div>{t("sc.token")}: <span className="font-mono text-foreground">{info.token_set ? (info.token_hint || "••••") : "—"}</span></div>
              {info.note && <div className="text-chart-4">{info.note}</div>}
            </div>
          </div>
          {TestButton}
        </div>
      ) : (
        <>
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm" data-testid="sc-state">
            <div className="flex flex-wrap items-center gap-2">
              <ShieldCheck className={`h-4 w-4 ${info.configured ? "text-primary" : "text-muted-foreground"}`} />
              <span className="font-medium">{info.configured ? t("sc.configured") : t("sc.notConfigured")}</span>
              {info.source && info.active === false && <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive" data-testid="sc-inactive">{t("sc.inactive")}</span>}
              {info.source === "tenant" && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary" data-testid="sc-source-tenant">{t("sc.sourceTenant")}</span>}
            </div>
            <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              {info.shop_domain && <div>{t("sc.domain")}: <span className="font-mono text-foreground">{info.shop_domain}</span></div>}
              <div>{t("sc.authMode")}: <span className="text-foreground">{info.auth_mode === "oauth" ? t("sc.authExternal") : t("sc.authSameOrg")}</span></div>
              {info.auth_mode === "client_credentials" && (
                <>
                  <div data-testid="sc-state-client-id">{t("sc.clientId")}: <span className="font-mono text-foreground">{info.client_id || "—"}</span></div>
                  <div data-testid="sc-state-client-secret">{t("sc.clientSecret")}: <span className="font-mono text-foreground">{info.client_secret_set ? (info.client_secret_hint || "••••") : "—"}</span></div>
                </>
              )}
              {info.auth_mode === "oauth" && <div data-testid="sc-state-oauth">{t("sc.oauthNotSetup")}</div>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>{t("sc.authMode")}</Label>
              <Select value={f.auth_mode} onValueChange={(v) => setF({ ...f, auth_mode: v })} disabled={!canEdit}>
                <SelectTrigger className="mt-1.5" data-testid="sc-auth-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client_credentials">{t("sc.authSameOrg")}</SelectItem>
                  <SelectItem value="oauth">{t("sc.authExternal")}</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="sm:col-span-2"><Label>{t("sc.domain")}</Label>
              <Input className="mt-1.5" value={f.shop_domain} disabled={!canEdit} placeholder="mein-shop.myshopify.com"
                onChange={(e) => setF({ ...f, shop_domain: e.target.value })} data-testid="sc-domain" /></div>
            <div><Label>{t("sc.environment")}</Label>
              <Select value={f.environment} onValueChange={(v) => setF({ ...f, environment: v })} disabled={!canEdit}>
                <SelectTrigger className="mt-1.5" data-testid="sc-environment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">{t("sc.envTest")}</SelectItem>
                  <SelectItem value="production">{t("sc.envProd")}</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label>{t("sc.apiVersion")}</Label>
              <Input className="mt-1.5" value={f.api_version} disabled={!canEdit}
                onChange={(e) => setF({ ...f, api_version: e.target.value })} data-testid="sc-api-version" /></div>
            <div className="sm:col-span-2"><Label>{t("sc.prodDomain")}</Label>
              <Input className="mt-1.5" value={f.prod_shop_domain} disabled={!canEdit} placeholder="produktivshop.myshopify.com"
                onChange={(e) => setF({ ...f, prod_shop_domain: e.target.value })} data-testid="sc-prod-domain" />
              <p className="mt-1 text-xs text-muted-foreground">{t("sc.prodDomainHint")}</p></div>

            {f.auth_mode === "client_credentials" && (
              <>
                <div className="sm:col-span-2"><Label>{t("sc.clientId")}</Label>
                  <Input className="mt-1.5" value={f.client_id} disabled={!canEdit} placeholder="1a2b3c4d…"
                    onChange={(e) => setF({ ...f, client_id: e.target.value })} data-testid="sc-client-id" /></div>
                <div className="sm:col-span-2"><Label>{t("sc.clientSecret")}</Label>
                  <Input type="password" autoComplete="new-password" className="mt-1.5" value={f.client_secret} disabled={!canEdit}
                    placeholder={info.client_secret_set ? t("sc.keepSecret") : "shpss_…"}
                    onChange={(e) => setF({ ...f, client_secret: e.target.value })} data-testid="sc-client-secret" /></div>
              </>
            )}
            {f.auth_mode === "oauth" && (
              <div className="sm:col-span-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground" data-testid="sc-oauth-hint">
                {t("sc.oauthHint")}
              </div>
            )}

            <label className="sm:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!f.active} disabled={!canEdit}
                onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="sc-active" />
              {t("common.active")}
            </label>
          </div>

          {f.auth_mode === "client_credentials" && (
            <p className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">{t("sc.secretNote")}</p>
          )}

          {TestButton}

          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={save} disabled={saving} data-testid="sc-save-btn">{t("common.save")}</Button>
              {info.source === "tenant" && (
                <Button variant="ghost" className="text-destructive" onClick={remove} data-testid="sc-delete-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> {t("sc.delete")}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
